import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword, resendPasswordResetCode } = useAuth();

  useEffect(() => {
    // Получаем параметры из URL
    const emailParam = searchParams.get('email');
    const codeParam = searchParams.get('code'); // Код из ответа (режим разработки)
    
    if (emailParam) {
      setEmail(emailParam);
    }
    
    // Если есть код в URL (режим разработки), автоматически заполняем поле
    if (codeParam) {
      setCode(codeParam);
    }

    // Если нет email - редирект на страницу запроса кода
    if (!emailParam) {
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!code || code.length !== 6) {
      setError('Код должен содержать 6 цифр');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setError('Пароль должен содержать строчные и заглавные буквы, а также цифры');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    setLoading(true);
    try {
      const result = await resetPassword(email, code, newPassword);
      
      if (result.success) {
        setSuccess(true);
        // Редирект на страницу входа через 3 секунды
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.message || 'Ошибка при сбросе пароля');
      }
    } catch (err) {
      setError(err.message || 'Ошибка при сбросе пароля');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;

    setError('');
    setLoading(true);
    try {
      const result = await resendPasswordResetCode(email);
      if (result.success) {
        // Код отправлен
        if (result.resetCode) {
          setCode(result.resetCode);
        }
      } else {
        setError(result.message || 'Ошибка при отправке кода');
      }
    } catch (err) {
      setError(err.message || 'Ошибка при отправке кода');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  if (success) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <h1>Пароль успешно изменен!</h1>
          <div className="success-message">
            <p>✅ Ваш пароль был успешно изменен.</p>
            <p>Перенаправляем на страницу входа...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <h1>Сброс пароля</h1>
        
        <p className="reset-password-info">
          Введите код, отправленный на <strong>{email}</strong>, и новый пароль
        </p>
        
        {code && searchParams.get('code') && (
          <div className="dev-code-notice" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '20px',
            textAlign: 'center',
            color: '#856404'
          }}>
            <strong>Режим разработки:</strong> Код был автоматически заполнен из ответа сервера
          </div>
        )}

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="code">Код подтверждения</label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              maxLength="6"
              className={error ? 'error' : ''}
              disabled={loading}
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Новый пароль</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Введите новый пароль"
              className={error ? 'error' : ''}
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Подтвердите пароль</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Повторите новый пароль"
              className={error ? 'error' : ''}
              disabled={loading}
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading || code.length !== 6 || !newPassword || !confirmPassword}
          >
            {loading ? 'Сброс пароля...' : 'Сбросить пароль'}
          </button>
        </form>

        <div className="resend-section">
          <p>Не получили код?</p>
          <button
            type="button"
            onClick={handleResend}
            disabled={loading || !email}
            className="btn-link"
          >
            Отправить код повторно
          </button>
        </div>

        <div className="back-to-login">
          <Link to="/login">Вернуться к входу</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
